import type { StateCreator } from "zustand";

import { getBrowserTimezone } from "~/core/generic/datetime-util";

import type { EnabledCcOrBccType, ZGlobalComposeEmailModal } from "../types";

export const globalComposeEmailModalSlice: StateCreator<
  ZGlobalComposeEmailModal
> = (set, get) => ({
  openGlobalComposeEmailModal: false,
  setOpenGlobalComposeEmailModal: openGlobalComposeEmailModal => {
    set({ openGlobalComposeEmailModal });
    if (!openGlobalComposeEmailModal) {
      set({
        mailBody: "",
        fromEmail: null,
        toEmails: [],
        ccEmails: [],
        bccEmails: [],
        subject: "",
        enabledCcOrBcc: "none",
        mail_id: undefined,
        defaultToEmail: undefined,
        contactsVariableData: [],
        fallbackVariablesData: {},
        isMassEmail: false,
        scheduledTime: null,
        previewContactEmail: null,
        draftEmailId: null,
        scheduledEmailId: null,
        scheduledTimezone: getBrowserTimezone(),
        overridenRecipientsData: [],
        fileAttachments: [],
        uploadingFiles: [],
      });
    }
  },

  setEmailData: emailData => {
    if (emailData.scheduledEmailId) {
      set({ scheduledEmailId: emailData.scheduledEmailId, draftEmailId: null });
    } else if (emailData.draftEmailId) {
      set({ draftEmailId: emailData.draftEmailId, scheduledEmailId: null });
    }
    set({
      fromEmail: emailData.from,
      subject: emailData.subject,
      mailBody: emailData.content,
      scheduledTime: emailData.schedule_starts_at
        ? new Date(emailData.schedule_starts_at).toISOString()
        : null,
      fallbackVariablesData: emailData.variable_fallbacks || {},
      toEmails: emailData.toEmails,
      ccEmails: emailData.cc ?? [],
      bccEmails: emailData.bcc ?? [],
      fileAttachments: emailData.attachments ?? [],
    });
  },

  mode: "maximized",
  setMode: mode => set({ mode }),

  mail_id: undefined,
  setMailId: mail_id => set({ mail_id }),

  enabledCcOrBcc: "none",
  setEnabledCcOrBcc: enabledCcOrBcc => set({ enabledCcOrBcc }),

  mailBody: "",
  setMailBody: mailBody => set({ mailBody }),

  fromEmail: null,
  setFromEmail: fromEmail => set({ fromEmail }),

  toEmails: [],
  setToEmails: toEmails => {
    const toEmailsOrderByAlphabetically = toEmails.sort(
      (a, b) => a.email?.localeCompare(b.email),
    );
    set({ toEmails: toEmailsOrderByAlphabetically });
  },

  ccEmails: [],
  setCcEmails: ccEmails => {
    set({ ccEmails });
    if (ccEmails.length) {
      const enabledCcOrBcc = get().enabledCcOrBcc;
      const isDisabled = enabledCcOrBcc === "none";
      const updatedEnabledCcOrBcc: EnabledCcOrBccType[] = isDisabled
        ? ["cc"]
        : ["cc", "bcc"];
      set({ enabledCcOrBcc: updatedEnabledCcOrBcc });
    }
  },

  bccEmails: [],
  setBccEmails: bccEmails => {
    set({ bccEmails });
    if (bccEmails.length) {
      const enabledCcOrBcc = get().enabledCcOrBcc;
      const isDisabled = enabledCcOrBcc === "none";
      const updatedEnabledCcOrBcc: EnabledCcOrBccType[] = isDisabled
        ? ["bcc"]
        : ["cc", "bcc"];
      set({ enabledCcOrBcc: updatedEnabledCcOrBcc });
    }
  },

  subject: "",
  setSubject: subject => set({ subject }),

  defaultToEmail: undefined,
  setDefaultToEmail: defaultToEmail => set({ defaultToEmail }),

  resetAllStates: () => {
    set({
      mailBody: "",
      toEmails: [],
      ccEmails: [],
      bccEmails: [],
      subject: "",
      enabledCcOrBcc: "none",
      mail_id: undefined,
      defaultToEmail: undefined,
    });
  },

  contactsVariableData: [],
  setContactsVariableData: contactsVariableData =>
    set({ contactsVariableData }),

  fallbackVariablesData: {},
  addFallbackVariablesData: (variableName, variableValue) =>
    set(state => ({
      fallbackVariablesData: {
        ...state.fallbackVariablesData,
        [variableName]: variableValue,
      },
    })),
  removeFallbackVariablesData: key =>
    set(state => {
      const { [key]: _, ...rest } = state.fallbackVariablesData || {};
      return { fallbackVariablesData: rest };
    }),

  isMassEmail: false,
  setIsMassEmail: isMassEmail => {
    if (isMassEmail) {
      set({ enabledCcOrBcc: "none", mode: "maximized" });
    }
    set({ isMassEmail });
  },

  scheduledTime: null,
  setScheduledTime: scheduledTime => set({ scheduledTime }),

  scheduledTimezone: getBrowserTimezone(),
  setScheduledTimezone: scheduledTimezone => set({ scheduledTimezone }),

  editorInstance: null,
  setEditorInstance: editorInstance => set({ editorInstance }),

  previewContactEmail: null,
  setPreviewContactEmail: previewContactEmail => set({ previewContactEmail }),

  draftEmailId: null,
  setDraftEmailId: draftEmailId => set({ draftEmailId }),
  scheduledEmailId: null,
  setScheduledEmailId: scheduledEmailId => set({ scheduledEmailId }),

  overridenRecipientsData: [],
  addToOverridenRecipientsData: recipientData => {
    const existingRecipientIndex = get().overridenRecipientsData.findIndex(
      recipient => recipient.recipient_email === recipientData.recipient_email,
    );

    if (existingRecipientIndex === -1) {
      set(state => ({
        overridenRecipientsData: [
          ...state.overridenRecipientsData,
          recipientData,
        ],
      }));
    } else {
      set(state => ({
        overridenRecipientsData: state.overridenRecipientsData.map(
          (recipient, index) =>
            index === existingRecipientIndex ? recipientData : recipient,
        ),
      }));
    }
  },
  removeFromOverridenRecipientsData: recipientEmail => {
    set(state => ({
      overridenRecipientsData: state.overridenRecipientsData.filter(
        recipient => recipient.recipient_email !== recipientEmail,
      ),
    }));
  },
  getOverridenRecipientDataByEmail: recipientEmail => {
    return get().overridenRecipientsData.find(
      recipient => recipient.recipient_email === recipientEmail,
    );
  },

  fileAttachments: [],
  setFileAttachments: fileAttachments => set({ fileAttachments }),
  replaceFileAttachmentUrl: (fileAttachment, fileUrl) => {
    set(state => {
      const fileAttachments = (state.fileAttachments as unknown as File[]).map(
        file => {
          if (file === fileAttachment) {
            return fileUrl;
          }
          return file;
        },
      ) as unknown as string[];

      return { fileAttachments };
    });
  },
  removeFileAttachment: file => {
    set(state => ({
      fileAttachments: (state.fileAttachments as unknown as File[]).filter(
        fileAttachment => fileAttachment !== file,
      ) as unknown as File[],
    }));
  },

  uploadingFiles: [],
  addToUploadingFiles: file => {
    set(state => ({
      uploadingFiles: [...state.uploadingFiles, file],
    }));
  },
  removeFromUploadingFiles: file => {
    set(state => ({
      uploadingFiles: state.uploadingFiles.filter(
        uploadingFile => uploadingFile !== file,
      ),
    }));
  },
});
